import { AgentActionPayload, PresetOption } from "@/types/createAgent";
import { CreateFormData } from "@/components/create/types";
import type { AgentPhase } from "@/types/createAgent";

export function getChatPlaceholder(_phase?: AgentPhase | null): string {
  return "Describe your dress…";
}

export function buildActionPayload(
  preset: PresetOption,
  source?: AgentActionPayload["source"]
): AgentActionPayload {
  return {
    type: "preset_action" as const,
    action: preset.action,
    actionId: preset.actionId,
    ...(preset.presetType ? { presetType: preset.presetType } : {}),
    ...(source ? { source } : {}),
    data: preset.data,
    actionData: preset.actionData,
  };
}

export { buildExplorePreset } from "@/lib/agent/footerActions";

export function buildModificationPrompt(preset: PresetOption): string {
  const data =
    preset.data && typeof preset.data === "object"
      ? (preset.data as Partial<CreateFormData>)
      : {};

  const promptParts: string[] = [];

  if (data.color) promptParts.push(`Change color to ${data.color}`);
  if (data.length) promptParts.push(`Set length to ${data.length}`);
  if (data.sleeve) promptParts.push(`Update sleeves to ${data.sleeve}`);
  if (data.sleeveAddons?.length) {
    promptParts.push(`Add sleeve details: ${data.sleeveAddons.join(", ")}`);
  }
  if (data.neckline) promptParts.push(`Change neckline to ${data.neckline}`);
  if (data.waistline) promptParts.push(`Update waistline to ${data.waistline}`);
  if (data.silhouette)
    promptParts.push(`Change silhouette to ${data.silhouette}`);
  if (data.fabric) promptParts.push(`Use fabric: ${data.fabric}`);

  if (data.backPrompt) promptParts.push(`Back design: ${data.backPrompt}`);
  if (promptParts.length > 0) {
    return promptParts.join("; ");
  }

  // Fall back to the preset label for simple modification suggestions
  return preset.label ? preset.label.trim() : "";
}

export function shouldTriggerUpload(preset: PresetOption): boolean {
  return (
    preset.actionId === "suggest_field" &&
    (preset.actionData?.suggestedField === "upload" ||
      preset.actionData?.suggestedField === "image_upload" ||
      preset.actionData?.suggestedField === "front_upload" ||
      preset.actionData?.suggestedField === "back_upload")
  );
}

export function getLoadingMessage(params: {
  isModifying: boolean;
  estimatedTimeRemaining: number | null | undefined;
  generationPhase: "idle" | "front" | "back" | "complete" | null;
}): string {
  const { isModifying, estimatedTimeRemaining, generationPhase } = params;

  if (isModifying) return "Applying your changes…";

  if (
    estimatedTimeRemaining !== null &&
    estimatedTimeRemaining !== undefined &&
    estimatedTimeRemaining <= 0
  ) {
    return "Adding final details…";
  }

  const getProgressPhase = () => {
    if (!estimatedTimeRemaining) return 0;
    if (estimatedTimeRemaining > 25) return 0;
    if (estimatedTimeRemaining > 18) return 1;
    if (estimatedTimeRemaining > 10) return 2;
    return 3;
  };

  const progressPhase = getProgressPhase();

  if (generationPhase === "front") {
    const frontMessages = [
      "Sketching your design…",
      "Refining the silhouette…",
      "Refining the details…",
      "Almost there…",
    ];
    return frontMessages[progressPhase] || "Sketching your design…";
  }
  if (generationPhase === "back") {
    const backMessages = [
      "Matching the front design…",
      "Refining the back…",
      "Finalizing details…",
      "Almost there…",
    ];
    return backMessages[progressPhase] || "Refining the back…";
  }

  return "Creating your design…";
}

export function formatTimeRemaining(
  seconds: number | null | undefined
): string {
  if (!seconds || seconds <= 0) return "";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return mins === 0
    ? `${secs}s`
    : `${mins}:${secs.toString().padStart(2, "0")}`;
}
